import { useRef } from 'react'
import styles from './styles.module.scss'

interface RichTextEditorProps {
    onChange: (html: string) => void
    placeholder?: string
}

interface ToolbarAction {
    label: string
    title: string
    command: string
    arg?: () => string | undefined
}

const TOOLBAR_ACTIONS: ToolbarAction[] = [
    { label: 'B', title: 'Bold', command: 'bold' },
    { label: 'I', title: 'Italic', command: 'italic' },
    { label: 'U', title: 'Underline', command: 'underline' },
    { label: '• List', title: 'Bullet list', command: 'insertUnorderedList' },
    { label: '1. List', title: 'Numbered list', command: 'insertOrderedList' },
    {
        label: 'Link',
        title: 'Insert link',
        command: 'createLink',
        arg: () => {
            const url = window.prompt('Link URL (https://...)')
            return url?.trim() || undefined
        },
    },
]

/**
 * Minimal dependency-free WYSIWYG editor (contentEditable + execCommand).
 * The produced HTML is sanitised server-side before being stored, so the
 * editor only needs to be a comfortable input surface. Remount (change `key`)
 * to clear it.
 */
export const RichTextEditor = ({ onChange, placeholder }: RichTextEditorProps) => {
    const editorRef = useRef<HTMLDivElement>(null)

    const emitChange = () => {
        onChange(editorRef.current?.innerHTML || '')
    }

    const runAction = (action: ToolbarAction) => {
        const arg = action.arg ? action.arg() : undefined
        if (action.arg && !arg) return
        editorRef.current?.focus()
        document.execCommand(action.command, false, arg)
        emitChange()
    }

    return (
        <div className={styles.editorWrapper}>
            <div className={styles.editorToolbar}>
                {TOOLBAR_ACTIONS.map((action) => (
                    <button
                        key={action.command}
                        type="button"
                        title={action.title}
                        className={styles.editorToolbarButton}
                        // Prevent the editor losing focus/selection on click
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => runAction(action)}
                    >
                        {action.label}
                    </button>
                ))}
            </div>
            <div
                ref={editorRef}
                className={styles.editorContent}
                contentEditable
                role="textbox"
                aria-multiline="true"
                data-placeholder={placeholder || 'Write your note...'}
                onInput={emitChange}
                onBlur={emitChange}
            />
        </div>
    )
}
